import { createElement } from 'react';
import type { ReactNode } from 'react';
import { searchField } from './field-query.js';
import { liensMultiples, lignesDeCellule } from './champs-types.js';
import { Pastille, RangeeDePastilles } from './rendus-de-champ.js';
import { estCouleur } from '@taki/data-model';
import type {
  Annuaires,
  Cell,
  Column,
  ColumnReference,
  FieldMap,
  Highlight,
  Option,
  ProjectedCell,
  ProjectedRow,
  RowHighlight,
  Tone,
  ViewBinding,
} from './contract.js';
import { collateur, formatDeDate } from '../lib/formats-intl.js';

/**
 * LA PROJECTION : enregistrement metier -> `ProjectedRow`.
 *
 * C'est l'etape qui fait tomber le generique. En entree, des `T` que seule
 * l'app connait, plus le binding qu'elle a declare ; en sortie, des lignes
 * neutres, forme UNIQUE que les cinq vues du core (et toute vue custom)
 * savent lire. Aucune vue ne voit jamais un `T`.
 *
 * Regles tenues ici :
 * - un role absent du binding ne produit rien (pas d'erreur, pas de valeur
 *   fabriquee) ;
 * - une valeur vide affiche le texte `empty` du champ, pas une chaine vide ;
 * - une valeur hors catalogue n'est JAMAIS jetee : elle devient une option
 *   neutre a son nom, sinon une ligne disparaitrait de l'ecran ;
 * - un role est editable SSI l'ecran a fourni `set()` dessus ; une COLONNE
 *   pareil, mais son editabilite vit dans sa cellule (`cells[key].editable`),
 *   jamais dans `row.editable`, reserve aux roles ;
 * - la pastille "Nouveau" (`isNew`) est POSEE ici, jamais devinee par une vue :
 *   c'est un fait sur la ligne, au meme titre que son titre ;
 * - la surbrillance (`highlight`) aussi : les regles du binding, ou a defaut
 *   « Nouveau » seule (`SURBRILLANCE_PAR_DEFAUT`), tranchees ligne par ligne.
 */

/** Les roles connus du core : tout le reste part dans `ProjectedRow.extra`. */
const KNOWN_ROLES = new Set<string>([
  'title',
  'subtitle',
  'avatar',
  'couleur',
  'photo',
  'status',
  'badges',
  'groupKey',
  'lane',
  'dateStart',
  'dateEnd',
]);

/**
 * LE LIBELLE DE LA COLONNE FOURRE-TOUT, celle des lignes dont le role a
 * catalogue (statut, colonne de kanban, couloir) n'a AUCUNE valeur.
 *
 * ELLE NOMME UNE ABSENCE, PAS UNE CATEGORIE, et c'est le mot que le reste du
 * produit emploie pour une case vide (« Non renseigné ») : c'est ICI qu'il est
 * ecrit, une fois, et la fiche, l'editeur et le tableau le lisent (`EMPTY_TEXT`
 * de `core/CellEditor.tsx` n'en est que le nom cote saisie). Le
 * pluriel « Autres » se lisait comme une valeur de plus du catalogue : un
 * annuaire dont la relation offre « Autre » montrait « Autre » et « Autres »
 * cote a cote, sans que rien ne dise que la seconde parle des fiches SANS
 * relation.
 *
 * LE TEXTE `vide` DU CHAMP PASSE DEVANT quand l'ecran en declare un
 * (`resolveOption`) : une app qui sait dire « Sans relation » le dit mieux que
 * le socle.
 */
export const OTHER_LABEL = 'Non renseigné';

/** Le ton d'une valeur qui n'est pas dans le catalogue du tenant. */
const OUT_OF_CATALOG_TONE: Tone = 'neutral';

/**
 * Les roles d'un binding, lus SANS le mensonge de type de l'intersection
 * `Partial<Record<FieldRole, ...>> & Record<string, ...>` (qui fait croire que
 * `roles.title` existe toujours). Un seul endroit ou la verite est retablie.
 */
function rolesOf<T>(binding: ViewBinding<T>): Record<string, FieldMap<T> | undefined> {
  return binding.roles as Record<string, FieldMap<T> | undefined>;
}

/**
 * LA COLONNE QUI PORTE LE CHAMP DU TITRE, quand l'ecran en declare une : celle
 * dont le chemin de stockage est celui du role. C'est elle qui sait si le champ
 * est une reference, un nombre, une date, et s'il est obligatoire : un role,
 * lui, ne dit que d'ou il lit.
 */
function colonneDuTitre<T>(binding: ViewBinding<T>, titre: FieldMap<T>): Column<T> | undefined {
  /* UN TITRE CALCULE (`{ champ, affichage: "libelle" }`) n'a pas de chemin de
     role, mais son unique partie sait quel champ elle lit : le mot du champ
     (« Article ») se retrouve par elle. */
  const chemin = titre.path ?? (titre.parts?.length === 1 ? titre.parts[0]?.path : undefined);
  if (chemin === undefined) return undefined;
  return (binding.fieldColumns ?? binding.columns ?? []).find((colonne) => colonne.path === chemin);
}

/**
 * LE TITRE REPREND-IL UNE FICHE LIEE, et laquelle : un titre fait d'UN champ qui
 * designe une fiche d'ailleurs, que la declaration soit ANCIENNE
 * (`title: { champ: "socle:assetId", modifiable: true }`) ou CALCULEE
 * (`title: { champ: "articleId", affichage: "libelle" }`). `undefined` pour tout
 * le reste (texte, catalogue, titre fait de plusieurs champs, fonction de l'app).
 *
 * UN TITRE EST UN TEXTE, JAMAIS UN MENU DE CHOIX (decision du fondateur,
 * 02/11/2026). Une fiche dont le titre s'ouvrait en selecteur de fiche liee
 * faisait croire qu'on y cherchait dans la fiche, deux gestes sous un mot. Un
 * titre qui reprend une fiche liee est donc un texte CALCULE, en lecture seule,
 * dont le clic OUVRE la fiche liee ; le lien se change dans son champ. Cette
 * fonction ne regarde pas `compose` ni `set` expres : une declaration ancienne
 * deja en base (`modifiable: true`) doit se lire comme la nouvelle, sans migration.
 *
 * UNE SEULE REPONSE POUR TOUS LES APPELANTS : la projection (la fiche rend le
 * titre cliquable), le moteur (le brouillon ne reclame pas le titre, les fenetres
 * ne le nomment pas) et le nom du modele (`titreEstTexte`) lisent la meme
 * fonction, sinon ils divergeraient.
 */
export function titreLieDe<T>(
  binding: ViewBinding<T> | undefined,
): { reference: ColumnReference; colonne: Column<T> | undefined; role: FieldMap<T> } | undefined {
  if (binding === undefined) return undefined;
  const role = rolesOf(binding)['title'];
  const parties = role?.parts ?? [];
  if (role === undefined || parties.length !== 1) return undefined;
  const reference = parties[0]?.reference;
  if (reference === undefined) return undefined;
  return { reference, colonne: colonneDuTitre(binding, role), role };
}

/**
 * LE TITRE EST-IL DU TEXTE, c'est-a-dire une case ou un NOM peut s'ecrire ?
 *
 * Seul un titre texte recoit le nom d'un modele (`avecTitreDuModele`). Une
 * reference attend un id, un catalogue une de ses clefs, un nombre ou une date
 * leur propre forme : y poser « Vaccin annuel chat » ecrit une valeur fausse que
 * rien ne refuse. Sans colonne pour le dire, le titre est du texte (le cas de
 * `name` : un natif que l'ecran ne catalogue pas).
 */
export function titreEstTexte<T>(binding: ViewBinding<T> | undefined): boolean {
  if (binding === undefined) return true;
  const role = rolesOf(binding)['title'];
  if (role === undefined) return true;
  if (role.options !== undefined) return false;
  if (role.parts?.some((partie) => partie.reference !== undefined) === true) return false;
  const colonne = colonneDuTitre(binding, role);
  if (colonne === undefined) return true;
  if (colonne.reference !== undefined) return false;
  return colonne.kind === undefined || colonne.kind === 'text' || colonne.kind === 'multiline';
}

/**
 * LE TITRE D'UN BROUILLON SE RECLAME-T-IL AVANT DE CREER ? `undefined` : non.
 * Sinon `{}`, le titre se tape.
 *
 * Un titre texte se reclame s'il s'ecrit (`set`). UN TITRE QUI REPREND UNE FICHE
 * LIEE (`titreLieDe`) OU QUI EST UN CHOIX (catalogue d'options) NE SE RECLAME
 * PLUS : ce n'est plus une case de l'en-tete, c'est un texte calcule (decision du
 * 02/11/2026, voir `titreLieDe`). Le champ lui-meme, dans sa carte, garde son
 * `required` : c'est lui que `manquantsDuBrouillon` reclame, sous son mot. En mode
 * Modeles, le titre est le NOM du modele, et la regle d'un titre texte vaut
 * toujours.
 */
export function titreAReclamer<T>(
  binding: ViewBinding<T> | undefined,
  modeles: boolean,
): { libelle?: string } | undefined {
  if (binding === undefined) return undefined;
  const role = rolesOf(binding)['title'];
  if (role === undefined) return undefined;
  if (!modeles && (titreLieDe(binding) !== undefined || role.options !== undefined)) return undefined;
  return role.set == null ? undefined : {};
}

/** Une valeur est-elle vide (rien a afficher) ? */
function isBlank(value: Cell | undefined): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Une CELLULE projetee, par opposition a une valeur brute : les deux passent
 * par `cellToText`, et seule la premiere porte un libelle d'annuaire.
 *
 * `Date` EST UN OBJET ELLE AUSSI, d'ou l'exclusion explicite : sans elle, une
 * date se lirait comme une cellule sans `value` et rendrait le texte vide.
 */
function estCellule(value: Cell | ProjectedCell | undefined): value is ProjectedCell {
  return typeof value === 'object' && value !== null && !(value instanceof Date) && 'value' in value;
}

/**
 * Met une valeur brute en texte. Les dates suivent la locale du poste (c'est
 * un souci d'affichage, jamais de stockage) ; les booleens parlent francais.
 *
 * UNE CELLULE ENTIERE S'Y PASSE AUSSI, et c'est ce qui tient la regle des
 * references : `label ?? value`, ecrit ICI et pas dans chaque appelant. Une
 * cellule de reference porte un ID en valeur ; chercher ou trier dessus ferait
 * classer les lignes par identifiant Firestore, sous des libelles qui disent
 * autre chose.
 */
export function cellToText(value: Cell | ProjectedCell | undefined, empty = ''): string {
  if (estCellule(value)) return cellToText(value.label ?? value.value, empty);
  if (isBlank(value)) return empty;
  /* Une date INVALIDE garde le mot du navigateur (« Invalid Date ») : le
     formateur garde, lui, leverait (`RangeError`). */
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? value.toLocaleDateString() : formatDeDate(undefined).format(value);
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  return String(value);
}

/**
 * Deux valeurs sont-elles la MEME ? La regle de l'ECRITURE, pas celle du tri :
 * elle decide si refermer un editeur doit ecrire (et cocher) ou ne rien faire du
 * tout. Une fausse coche sur une valeur inchangee serait un mensonge, et la
 * meme regle doit valoir cote vue (qui referme) et cote core (qui ecrit), sinon
 * l'un des deux coche sans que l'autre ecrive.
 *
 * Le vide sous toutes ses formes ('', null, undefined) est une seule et meme
 * absence de valeur ; deux dates se comparent a l'instant, jamais par identite.
 */
export function sameCell(a: Cell | undefined, b: Cell | undefined): boolean {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank || bBlank) return aBlank && bBlank;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  return a === b;
}

/**
 * Compare deux valeurs pour le tri. Les vides finissent TOUJOURS en bas, quel
 * que soit le sens : une ligne sans echeance ne remonte pas en tete parce
 * qu'on a clique deux fois.
 */
export function compareCells(a: Cell | undefined, b: Cell | undefined): number {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return collateur(undefined, { numeric: true, sensitivity: 'base' }).compare(String(a), String(b));
}

/**
 * LE LIBELLE D'UNE VALEUR DE CATALOGUE, pour une COLONNE (« Commerce » pour
 * `retail`). `undefined` des que la question ne se pose pas : colonne sans
 * catalogue, case vide, ou valeur que le catalogue ne connait pas.
 *
 * POURQUOI LA CELLULE LE PORTE, alors que `render` affiche deja le mot : le
 * rendu est un noeud React, et ni la recherche ni le tri ne savent le lire. Le
 * libelle, lui, traverse les deux (`cellToText`, `sortValueOf`), exactement
 * comme celui d'une colonne de REFERENCE. Sans lui, une liste triee par secteur
 * suivait l'ordre des clefs ANGLAISES sous des libelles francais (« Commerce »
 * apres « Institution »), et chercher le mot affiche ne rendait rien.
 */
export function libelleDeCatalogue<T>(column: Column<T>, value: Cell): string | undefined {
  if (column.options === undefined || isBlank(value)) return undefined;
  const cherche = value instanceof Date ? value.toISOString() : String(value);
  return column.options().find((option) => option.value === cherche)?.label;
}

/**
 * Resout une valeur dans le catalogue d'un role (statut, colonne de kanban,
 * couloir). Trois cas, aucun ne perd la ligne :
 * - valeur vide : l'option fourre-tout (le texte `empty` du champ, sinon
 *   « Non renseigne ») ;
 * - valeur au catalogue : l'option du tenant, avec son libelle et son ton ;
 * - valeur hors catalogue : une option neutre a son nom.
 */
export function resolveOption<T>(value: Cell | undefined, field: FieldMap<T> | undefined): Option {
  if (isBlank(value)) {
    return { value: '', label: field?.empty ?? OTHER_LABEL, tone: OUT_OF_CATALOG_TONE };
  }
  const raw = value instanceof Date ? value.toISOString() : String(value);
  const catalog = field?.options?.() ?? [];
  const found = catalog.find((option) => option.value === raw);
  if (found) return found;
  return { value: raw, label: cellToText(value), tone: OUT_OF_CATALOG_TONE };
}

/** Toutes les valeurs d'un champ : `getMany` s'il existe, sinon `get` seul. */
function valuesOf<T>(record: T, field: FieldMap<T>): Cell[] {
  if (field.getMany) return field.getMany(record);
  return [field.get(record)];
}

/** La forme d'une DATE PURE du socle : un jour, sans heure et sans fuseau. */
const JOUR_PUR = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Met une valeur en Date quand c'en est une, sinon rien (pas de date inventee).
 *
 * UNE DATE PURE SE LIT EN HEURE LOCALE, ET C'EST TOUT LE PIEGE de cette
 * fonction. `new Date('2026-09-06')` interprete la chaine en UTC (c'est la
 * norme ECMAScript pour la forme courte), donc a l'est de Greenwich le jour se
 * decale de quelques heures, et a l'ouest il recule d'un jour entier. Deux
 * consequences se voyaient a l'ecran, sur toute app qui pose `dateStart` sur un
 * champ `date` (la porte Locations d'Inventaire, app sortie du produit le
 * 11/10/2026, en etait le cas) :
 *  - le calendrier ecrivait « 02:00 » devant l'evenement, parce que minuit UTC
 *    n'est pas minuit local et que `isAllDay` (`CalendarView.tsx`) ne
 *    reconnaissait plus une journee entiere ;
 *  - a l'ouest, la barre partait la VEILLE du jour saisi.
 *
 * On construit donc le jour avec le constructeur LOCAL, a minuit : c'est la
 * forme que `isAllDay` attend, et c'est la doctrine des dates du socle
 * (`AGENTS.md` section 7 : une date pure est une chaine `YYYY-MM-DD`, jamais un
 * instant). Toute autre chaine reste une date-heure et garde son analyse
 * normale : un instant ISO complet porte son fuseau, il n'y a rien a corriger.
 */
function toDate(value: Cell | undefined): Date | undefined {
  if (value instanceof Date) return value;
  if (typeof value === 'string' && value !== '') {
    if (JOUR_PUR.test(value)) {
      const annee = Number(value.slice(0, 4));
      const mois = Number(value.slice(5, 7));
      const jour = Number(value.slice(8, 10));
      return new Date(annee, mois - 1, jour);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return undefined;
}

/* ------------------------------------------------------------------ */
/* La pastille "Nouveau" : le CALCUL, jamais le rendu                  */
/* ------------------------------------------------------------------ */

/**
 * CE QUE LA PROJECTION DOIT SAVOIR pour allumer `isNew`, tel que `useScreen`
 * le lui donne (la memoire locale au poste vit dans `newRecords.ts`).
 *
 * ABSENT = personne ne demande la pastille, et `isNew` retombe sur `false`.
 * C'est ce qui garde `projectRecord` et `projectRecords` retro-compatibles
 * pour leurs appelants d'avant ce chantier (la fiche, qui ne porte jamais de
 * pastille : l'ouvrir est justement ce qui l'eteint).
 */
export interface NewRecordsInput {
  /** Date de reference de la porte, en millisecondes. Rien d'anterieur n'est neuf. */
  since: number;
  /** Les ids deja ouverts sur ce poste depuis cette date. */
  seen: ReadonlySet<string>;
}

/**
 * `createdAt` d'un enregistrement quelconque, en millisecondes.
 *
 * C'est un CHAMP STANDARD pose par le SDK sur les 57 familles
 * (packages/sdk/src/standard-fields.ts) : on le lit donc par son NOM, jamais
 * par un role de binding. Aucun ecran n'a a declarer quoi que ce soit pour que
 * la pastille marche, et aucun n'a le pouvoir de la fausser en projetant une
 * autre date sur un role.
 *
 * Lecture DEFENSIVE, comme `isArchived` cote `useScreen` : le core ne connait
 * pas `T`, et un chargeur de decor (un banc d'essai) n'a pas de `createdAt` du tout.
 * Sans date lisible, la ligne n'est JAMAIS neuve : mieux vaut ne rien signaler
 * que signaler au hasard.
 */
function createdAtOf(record: unknown): number | null {
  const value = (record as { createdAt?: unknown } | null)?.createdAt;
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : time;
  }
  // Le SDK rend des `Date`. Une chaine ISO ou un nombre arrivent d'un chargeur
  // qui n'est pas passe par lui (decor en dur, cache reserialise).
  if (typeof value === 'string' || typeof value === 'number') {
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
  }
  return null;
}

/**
 * UNE FICHE EST RECENTE si elle a ete creee APRES la date de reference de la
 * porte. UNE SEULE condition : ce que l'utilisateur a ouvert reste recent.
 *
 * Elle ne sort PLUS sur la ligne projetee : depuis le retrait du repli du
 * lisere (07/08/2026, voir core/NewBadge.tsx), son seul role est de servir de
 * premiere condition a `isNew`. Elle reste une fonction a part parce qu'elle
 * repond a une question differente ("creee apres la reference") de celle que
 * pose `isNew` ("creee apres la reference ET jamais ouverte").
 *
 * Comparaison STRICTE : la date de reference est posee a la premiere visite,
 * donc ce qui existait a cet instant precis n'est pas une nouveaute.
 */
export function isRecordRecent(record: unknown, input?: NewRecordsInput): boolean {
  if (input == null) return false;
  const createdAt = createdAtOf(record);
  return createdAt != null && createdAt > input.since;
}

/**
 * UNE FICHE EST NOUVELLE si elle est RECENTE ET que son id n'est pas dans
 * l'ensemble des fiches deja ouvertes.
 *
 * Les deux conditions comptent, et aucune ne suffit : la premiere empeche la
 * toute premiere visite d'allumer les 500 lignes deja en base, la seconde
 * eteint ce que l'utilisateur a ouvert. C'est cette quantite-la, et elle seule,
 * que les vues RENDENT (lisere et pastille).
 */
export function isRecordNew(record: unknown, id: string, input?: NewRecordsInput): boolean {
  if (input == null) return false;
  if (input.seen.has(id)) return false;
  return isRecordRecent(record, input);
}

/* ------------------------------------------------------------------ */
/* Les references : un id dans la cellule, un nom sous les yeux         */
/* ------------------------------------------------------------------ */

/**
 * LA CLEF D'UN JEU dans la carte des annuaires (`Annuaires`).
 *
 * FONCTION PURE ET EXPORTEE EXPRES : celui qui REMPLIT la carte (l'hote, qui
 * charge les annuaires) et celui qui la LIT (cette projection) doivent
 * fabriquer la meme chaine. Deux formats, meme proches, ne se croisent jamais :
 * la lecture rend `undefined`, la colonne affiche des ids, et rien ne le dit.
 *
 * DEUX JEUX D'UNE MEME FAMILLE SONT DEUX ANNUAIRES : `assets/materiel` n'est
 * pas `assets/vehicule`, et les melanger ferait resoudre le nom d'une fiche que
 * la porte ne montre pas.
 */
export function cleDeJeu(family: string, type?: string): string {
  return type === undefined ? family : `${family}/${type}`;
}

/**
 * Ce que la cellule dit d'un id que l'annuaire, pourtant COMPLET, ne connait pas.
 *
 * ELLE NOMME LE CAS LE PLUS FREQUENT EN PREMIER (09/09/2026). L'annuaire charge
 * les ARCHIVES (`archived: 'all'`), donc une fiche archivee y est et se nomme :
 * ce qu'il ne charge JAMAIS, c'est une fiche SUPPRIMEE, que le SDK retire cote
 * client sur ce mode. Un id inconnu d'un annuaire complet est donc, dans la
 * quasi-totalite des cas, une fiche supprimee, et « introuvable » tout court
 * envoyait chercher une panne de lecture la ou il y a eu un geste.
 * « ou introuvable » reste : le socle ne peut pas distinguer les deux (les deux
 * se lisent pareil, par une absence), et affirmer la suppression seule serait
 * affirmer plus qu'on ne sait.
 */
export const FICHE_LIEE_INTROUVABLE = 'Supprimée ou introuvable';

/** Ce qu'elle dit d'une fiche qui existe, mais qui a ete archivee. */
export const FICHE_LIEE_ARCHIVEE = 'Archivée';

/**
 * Ce qu'un selecteur dit de la valeur DEJA POSEE qui ne passe plus la condition
 * des choix du champ (`ColumnReference.choixPermis`) : elle reste en tete de
 * liste, attenuee, avec ce mot. Un libelle court, pas une phrase.
 */
export const FICHE_HORS_DES_CHOIX = 'Hors des choix';

/**
 * CE QU'UNE COLONNE DE REFERENCE AJOUTE A SA CELLULE : le libelle de la fiche
 * designee, et le rendu qui va avec.
 *
 * QUATRE ETATS, ET ILS NE SE CONFONDENT PAS :
 * - l'annuaire n'est pas charge (aucune clef pour ce jeu) : l'ID NU, sans
 *   libelle. C'est l'etat de tous les ecrans d'avant ce lot, qui ne passent
 *   aucun annuaire : rien ne change pour eux.
 * - il connait l'id : le TITRE, et il devient le `label` de la cellule, donc ce
 *   qui se cherche, se trie et s'exporte. Une fiche ARCHIVEE garde son titre et
 *   son libelle (elle existe, on la NOMME) : seul son rendu s'attenue, avec son
 *   mot au survol. Sans ca, une reference vers une archive se lirait comme une
 *   fiche effacee.
 * - il ne connait pas l'id ET il est COMPLET : l'id, ATTENUE, avec sa raison au
 *   survol. Une fiche liee effacee laisse un id derriere elle, et une cellule
 *   vide ferait croire a un champ non renseigne.
 * - il ne connait pas l'id et il est INCOMPLET : l'id NU, sans affirmation.
 *   Sous le plafond, un id manquant peut simplement etre hors fenetre, et
 *   affirmer « introuvable » ferait chercher une fiche effacee qui existe.
 *   MAIS un id que la surface a DEMANDE ne reste pas hors fenetre : sa fiche se
 *   lit par lot (`Annuaire.enAttente`). Le temps de la lecture la case est
 *   VIDE, puis elle prend le titre ; une fiche supprimee retombe sur l'id nu.
 *
 * `complet` EST LU SUR L'ANNUAIRE, PAS AILLEURS, et c'est ce qui rend cette
 * regle unique : la fiche relisait l'etat du magasin pour corriger ce que la
 * projection affirmait ici (`noeudDeReference`, `views/core/RecordView.tsx`).
 * Deux endroits pour une meme phrase, et le tableau disait « introuvable » la
 * ou la fiche montrait l'id nu, sur la meme donnee.
 */
function referenceDeCellule(
  reference: ColumnReference,
  value: Cell,
  annuaires: Annuaires | undefined,
  /**
   * LA CELLULE PORTE PLUSIEURS IDS, un par ligne (un champ `liens`). Chacun se
   * nomme par la MEME regle que l'id seul, ci-dessous, et devient sa pastille ;
   * le libelle de la cellule est la suite des noms, UNE LIGNE PAR ID, dans
   * l'ordre des ids : c'est ce que la fiche relit pour nommer chaque pastille
   * (`ChampsRiches.tsx`), et ce que la recherche et le tri lisent.
   */
  plusieurs = false,
): { label?: string; node?: ReactNode } {
  if (isBlank(value)) return {};
  const annuaire = annuaires?.get(cleDeJeu(reference.family, reference.type));
  if (plusieurs) {
    const fiches = lignesDeCellule(value).map((id): { id: string; titre: string; raison: string | undefined; attente?: boolean } => {
      const fiche = annuaire?.fiches.get(id);
      if (fiche !== undefined) return { id, titre: fiche.title, raison: fiche.archive ? FICHE_LIEE_ARCHIVEE : undefined };
      /* EN COURS DE LECTURE : ni l'id nu ni une pastille vide (`Annuaire.enAttente`). */
      if (annuaire?.enAttente?.has(id) === true) return { id, titre: '', raison: undefined, attente: true };
      return { id, titre: id, raison: annuaire?.complet === true ? FICHE_LIEE_INTROUVABLE : undefined };
    });
    return {
      ...(annuaire === undefined ? {} : { label: fiches.map((fiche) => fiche.titre).join('\n') }),
      node: createElement(
        RangeeDePastilles,
        null,
        ...fiches.filter((fiche) => fiche.attente !== true).map((fiche) =>
          createElement(Pastille, {
            key: fiche.id,
            libelle: fiche.titre,
            attenue: fiche.raison !== undefined,
            ...(fiche.raison === undefined ? {} : { titre: fiche.raison }),
            className: 'border-accent-tint-border bg-accent-tint font-semibold text-ink-900',
          }),
        ),
      ),
    };
  }
  /* UNE REFERENCE A REPLI NE MONTRE JAMAIS SA VALEUR (une personne : son
     `personId` ou son uid ne disent rien a personne). Tant que l'annuaire
     n'est pas la, la case reste VIDE plutot que de montrer l'id ; un id qu'il
     ne nomme pas dit le mot du repli, attenue. */
  const repli = reference.repli;
  if (annuaire === undefined) return repli === undefined ? {} : { label: '', node: '' };
  const fiche = annuaire.fiches.get(String(value));
  if (fiche === undefined) {
    if (repli !== undefined) {
      return {
        label: repli,
        node: createElement('span', { className: 'text-ink-300', title: repli }, repli),
      };
    }
    /* SA FICHE SE LIT A LA DEMANDE (hors fenetre) : la case reste VIDE le temps
       de la lecture, comme celle d'une reference a repli, et jamais l'id nu. */
    if (annuaire.enAttente?.has(String(value)) === true) return { label: '', node: '' };
    if (!annuaire.complet) return {};
    return {
      node: createElement(
        'span',
        { className: 'text-ink-300', title: FICHE_LIEE_INTROUVABLE },
        cellToText(value),
      ),
    };
  }
  if (fiche.archive) {
    return {
      label: fiche.title,
      node: createElement(
        'span',
        { className: 'text-ink-300', title: FICHE_LIEE_ARCHIVEE },
        fiche.title,
      ),
    };
  }
  return { label: fiche.title, node: fiche.title };
}

/**
 * LE TEXTE D'UN ROLE DE PREMIERE LIGNE (`title`, `subtitle`), tel que la ligne
 * l'affiche.
 *
 * `ProjectedRow.title` et `.subtitle` sont des CHAINES, et jusqu'ici elles
 * venaient de la valeur brute : le `render` qu'un habillage declare installe
 * sur le role (`views/screens.ts`, source `habillage`) etait pose, valide, et
 * jamais lu. Une saisie d'heures dont le sous-titre est son instant s'ecrivait
 * donc « 9/15/2026 », a la locale du navigateur et sans son heure, quand la
 * colonne du tableau, habillee pareil, disait « 15/09/2026 14:00 ». Une app
 * codee contournait par une valeur nommee (`fn`) ; une app DECLAREE n'a pas
 * de fonction a ecrire, et c'est le socle qui doit tenir sa promesse.
 *
 * SEUL UN RENDU TEXTE EST PRIS : la ligne porte une chaine, pas un noeud. Un
 * habillage d'app qui rend un composant garde le comportement d'avant (valeur
 * brute), sans casser la ligne. Et c'est CE TEXTE que le tri sur le role lit
 * (`sortValueOf`) : c'etait deja le mot affiche, il le reste.
 *
 * UN ROLE QUI PORTE UN ID AFFICHE LE NOM DE LA FICHE, par la MEME regle que la
 * cellule (`referenceDeCellule`) : un sous-titre « Client » stocke l'id d'un
 * contact, et sans ceci la liste disait `tacs-hotel-bains` la ou le tableau,
 * une vue plus loin, disait « TACS Hotel des Bains ». Le role porte ses
 * PARTIES (`FieldMap.parts`), chacune avec la porte qu'elle designe ; celles
 * qui en designent une sont nommees, les autres gardent leur valeur, et le
 * tout est joint comme le compose l'etait deja (`joindreParties`). Ce que
 * l'annuaire ne sait pas (pas charge, id inconnu) reste l'id nu, exactement
 * comme dans une cellule. SANS PARTIE QUI DESIGNE, ou sans annuaire, le role
 * garde son chemin d'avant, a l'identique.
 *
 * L'HABILLAGE PASSE DEVANT, comme sur une colonne (`render` garde la main sur
 * le rendu) : une app qui habille un role sait ce qu'elle fait.
 *
 * EXPORTE, parce que le titre d'une fiche se lit AILLEURS que sur sa ligne :
 * le nom d'une REFERENCE vers elle (`referenceVers`, `views/screens.ts`), donc
 * l'annuaire, le fil d'Ariane et le titre d'une saisie qui vise une tache.
 * Lire `get` a la main y montrait la clef stockee d'un titre a catalogue
 * (`redaction` au lieu de « Rédaction », lot du 14/10/2026).
 */
export function texteDuRole<T>(field: FieldMap<T>, record: T, annuaires?: Annuaires): string {
  if (field.render !== undefined) {
    const rendu = field.render(record);
    if (typeof rendu === 'string' && rendu !== '') return rendu;
  }
  if (field.compose) return cellToText(field.compose(record, part => {
    const value = part.get(record);
    return part.reference ? referenceDeCellule(part.reference, value, annuaires).label ?? value : value;
  }), field.empty ?? '');
  const parts = field.parts;
  /* UNE PARTIE A REPLI SE NOMME MEME SANS ANNUAIRE : sans cette clause, un role
     « Cree par » projete sans annuaires montrerait l'uid brut. */
  const nommer =
    parts !== undefined &&
    (annuaires !== undefined
      ? parts.some((part) => part.reference !== undefined)
      : parts.some((part) => part.reference?.repli !== undefined));
  if (nommer && parts !== undefined) {
    const texte = joindreParties(
      parts.map((part) => {
        const value = part.get(record);
        const label =
          part.reference === undefined
            ? undefined
            : referenceDeCellule(part.reference, value, annuaires).label;
        return label ?? cellToText(value);
      }),
    );
    if (texte !== '') return texte;
    /* Vide parce qu'une partie a repli attend son annuaire : le texte vide du
       role, jamais la valeur brute juste en dessous. */
    if (parts.some((part) => part.reference?.repli !== undefined)) return field.empty ?? '';
  }
  /* UN ROLE SUR UN CHAMP A CATALOGUE DIT LE LIBELLE DU CHOIX, comme une cellule
     (`docs/VUES-CONTRAT.md`, 20.2) : une saisie titree par son Service se lit
     « Production », jamais la valeur stockee `production`. */
  const value = field.get(record);
  if (!isBlank(value) && field.options !== undefined) {
    const brut = value instanceof Date ? value.toISOString() : String(value);
    const choix = field.options().find((option) => option.value === brut);
    if (choix !== undefined) return choix.label;
  }
  return cellToText(value, field.empty ?? '');
}

/**
 * L'OPTION D'UN GROUPE OU D'UN COULOIR, NOMMEE QUAND ELLE DESIGNE UNE FICHE.
 *
 * Un kanban groupe par « Intervenant » range ses colonnes par la VALEUR
 * stockee, un `personId` : sans cette etape, l'en-tete de colonne, la
 * recherche et le catalogue (`catalogOf`) montraient cet identifiant. La
 * VALEUR reste l'id (c'est la clef du groupe, et ce qu'un deplacement ecrit),
 * seul le LIBELLE devient le nom, par la meme regle que la cellule
 * (`referenceDeCellule`). Un catalogue declare garde la main : une valeur
 * qu'il connait n'est pas une reference.
 */
function optionNommee<T>(
  value: Cell | undefined,
  field: FieldMap<T> | undefined,
  annuaires: Annuaires | undefined,
): Option {
  const option = resolveOption(value, field);
  const partie = field?.parts?.length === 1 ? field.parts[0] : undefined;
  if (partie?.reference === undefined || isBlank(value ?? null)) return option;
  if (field?.options?.().some((connue) => connue.value === option.value) === true) return option;
  const label = referenceDeCellule(partie.reference, value ?? null, annuaires).label;
  return label === undefined ? option : { ...option, label };
}

/**
 * CE QUE LE SOCLE MET ENTRE LES PARTIES d'un role compose, et la jointure
 * elle-meme : les parties vides tombent, les autres se suivent.
 *
 * Separateur historique pour `source.champs` sans option. Une composition
 * declarative peut fournir son separateur ; get et projection nommee passent
 * alors par le meme evaluateur (`data-model/composition-affichage.ts`).
 */
export const SEPARATEUR_COMPOSE = ' · ';

export function joindreParties(textes: readonly string[]): string {
  return textes.filter((texte) => texte !== '').join(SEPARATEUR_COMPOSE);
}

/**
 * LA REGLE DE SURBRILLANCE QUAND L'ECRAN N'EN DECLARE AUCUNE : « Nouveau », en
 * accent, et rien d'autre. C'est ce qui garde la marque des fiches neuves sur
 * TOUT kanban, declare ou code a la main, sans que la carte en fasse un cas a
 * part (`docs/socle/cartes-kanban.md`, section 2).
 */
export const SURBRILLANCE_PAR_DEFAUT: readonly Highlight<unknown>[] = Object.freeze([
  Object.freeze({ when: 'nouveau', color: 'accent', label: 'Nouveau' } as const),
]);

/**
 * LA SURBRILLANCE D'UNE LIGNE : les regles dans l'ordre, la PREMIERE vraie
 * donne la couleur, chaque vraie qui a un label pose son etiquette. `null`
 * quand aucune n'est vraie : la vue n'a rien a dessiner, et ne devine rien.
 */
function surbrillanceDe<T>(
  record: T,
  isNew: boolean,
  regles: readonly Highlight<T>[] | undefined,
): RowHighlight | null {
  let surbrillance: RowHighlight | null = null;
  for (const regle of regles ?? (SURBRILLANCE_PAR_DEFAUT as readonly Highlight<T>[])) {
    const vraie = regle.when === 'nouveau' ? isNew : regle.when(record);
    if (!vraie) continue;
    surbrillance ??= { color: regle.color, labels: [] };
    if (regle.label !== undefined) surbrillance.labels.push({ label: regle.label, color: regle.color });
  }
  return surbrillance;
}

/**
 * `titreLie` DE LA LIGNE : la fiche que le titre reprend, quand il en reprend
 * une (`titreLieDe`), de quoi l'OUVRIR et rien de plus. Rien sinon, et rien non
 * plus pour une ligne sans binding.
 *
 * Aucun `ecrivable`, aucun moyen de changer le lien d'ici : un titre est un
 * texte, le lien se change dans son champ (decision du 02/11/2026).
 */
function titreLieDeLaLigne<T>(
  binding: ViewBinding<T> | undefined,
  record: T,
): Pick<ProjectedRow, 'titreLie'> {
  const titre = titreLieDe(binding);
  if (titre === undefined) return {};
  const { reference, colonne, role } = titre;
  const brut = titre.role.parts?.[0]?.get(record) ?? role.get(record);
  const libelle = colonne?.label ?? role.label;
  return {
    titreLie: {
      reference: {
        ...(reference.door === undefined ? {} : { door: reference.door }),
        family: reference.family,
        ...(reference.type === undefined ? {} : { type: reference.type }),
        ...(reference.modeles === true ? { modeles: true } : {}),
      },
      valeur: isBlank(brut) || typeof brut === 'object' ? '' : String(brut),
      ...(libelle === undefined || libelle === '' ? {} : { libelle }),
    },
  };
}

/**
 * `titreSansValeur` DE LA LIGNE : ce que le titre d'un BROUILLON dit, en gris,
 * tant que la fiche liee ou le choix qu'il reprend n'a pas de valeur. Le `vide`
 * de la source quand elle en declare un, sinon le mot du champ (« Article »).
 * Rien pour un titre texte, ni quand le titre a une valeur.
 */
function titreSansValeurDeLaLigne<T>(
  binding: ViewBinding<T> | undefined,
  record: T,
): Pick<ProjectedRow, 'titreSansValeur'> {
  const role = binding === undefined ? undefined : rolesOf(binding)['title'];
  if (binding === undefined || role === undefined) return {};
  const lie = titreLieDe(binding);
  if (lie === undefined && role.options === undefined) return {};
  const valeur = lie === undefined ? role.get(record) : (role.parts?.[0]?.get(record) ?? null);
  if (!isBlank(valeur)) return {};
  const libelle = colonneDuTitre(binding, role)?.label ?? role.label;
  const mot = role.empty ?? libelle;
  return mot === undefined || mot === '' ? {} : { titreSansValeur: mot };
}

/** Projette UN enregistrement en ligne neutre, selon le binding de la vue active. */
export function projectRecord<T>(
  record: T,
  id: string,
  binding: ViewBinding<T> | undefined,
  /**
   * Facultatif, et faux par defaut : les appelants d'avant ce chantier
   * (fiche, vue custom qui reprojette) gardent exactement leur comportement.
   */
  isNew = false,
  /**
   * LES ANNUAIRES CHARGES, pour les colonnes de REFERENCE. Facultatif, et
   * absent chez tous les appelants d'avant ce lot : sans eux (ou sans celui du
   * jeu vise), une cellule de reference garde l'id nu, exactement comme avant.
   */
  annuaires?: Annuaires,
): ProjectedRow {
  const roles = binding ? rolesOf(binding) : {};
  const columns: Column<T>[] = binding?.columns ?? [];

  const titleField = roles['title'];
  const subtitleField = roles['subtitle'];
  const avatarField = roles['avatar'];
  const couleurField = roles['couleur'];
  const photoField = roles['photo'];
  const statusField = roles['status'];
  const badgesField = roles['badges'];
  const groupField = roles['groupKey'];
  const laneField = roles['lane'];
  const startField = roles['dateStart'];
  const endField = roles['dateEnd'];

  // Le titre ne peut pas etre vide : une ligne sans identite est illisible.
  const title = titleField ? texteDuRole(titleField, record, annuaires) : '';

  const valeurDuTitre = titleField?.options !== undefined ? titleField.get(record) : undefined;
  const row: ProjectedRow = {
    id,
    title: title === '' ? id : title,
    ...(titleField?.options === undefined
      ? {}
      : { titreAChoix: isBlank(valeurDuTitre) ? '' : String(valeurDuTitre) }),
    ...titreLieDeLaLigne(binding, record),
    ...titreSansValeurDeLaLigne(binding, record),
    badges: [],
    groups: [],
    lanes: [],
    extra: {},
    cells: {},
    editable: {},
    isNew,
    highlight: surbrillanceDe(record, isNew, binding?.highlights),
  };

  if (subtitleField) {
    row.subtitle = texteDuRole(subtitleField, record, annuaires);
  }
  if (avatarField) {
    const value = texteDuRole(avatarField, record, annuaires);
    if (!isBlank(value)) row.avatar = value;
  }
  if (couleurField || photoField) row.identite = true;
  if (couleurField) {
    // Une valeur qui n'est ni une clef de la grille ni un hex est ECARTEE : un
    // anneau gris silencieux serait pire que pas d'anneau.
    const valeur = couleurField.get(record);
    if (estCouleur(valeur)) row.couleur = valeur;
  }
  if (photoField) {
    const valeur = photoField.get(record);
    if (typeof valeur === 'string' && valeur.trim() !== '') row.photo = valeur;
  }
  if (statusField) {
    row.status = resolveOption(statusField.get(record), statusField);
  }
  if (badgesField) {
    // Contrairement aux couloirs, une ligne SANS etiquette est le cas normal :
    // les valeurs vides sont ecartees, jamais resolues en fourre-tout.
    row.badges = valuesOf(record, badgesField)
      .filter((value) => !isBlank(value))
      .map((value) => resolveOption(value, badgesField));
  }
  if (groupField) {
    row.groups = valuesOf(record, groupField).map((value) =>
      optionNommee(value, groupField, annuaires),
    );
  }
  if (laneField) {
    row.lanes = valuesOf(record, laneField).map((value) =>
      optionNommee(value, laneField, annuaires),
    );
  }
  if (startField) {
    const start = toDate(startField.get(record));
    if (start) {
      row.start = start;
      if (startField.instant !== undefined) row.startFuseau = startField.instant.fuseau;
    }
  }
  if (endField) {
    const end = toDate(endField.get(record));
    if (end) row.end = end;
  }

  // Roles libres (vue custom) : la valeur brute, sans interpretation.
  for (const [name, field] of Object.entries(roles)) {
    if (field == null) continue;
    row.editable[name] = typeof field.set === 'function';
    if (KNOWN_ROLES.has(name)) continue;
    row.extra[name] = field.get(record);
  }

  // Colonnes : la valeur comparable, son rendu, et son editabilite, cote a
  // cote. C'est le SEUL endroit du repo qui construit une cellule.
  for (const column of columns) {
    const value = column.value(record);
    // UNE COLONNE DE REFERENCE PORTE UN ID : l'annuaire de son jeu en fait un
    // nom. L'habillage de l'app garde la main sur le rendu (c'est le contrat
    // de `render`), mais le LIBELLE est pose quand meme : c'est lui qui sert a
    // chercher et a trier, et personne d'autre ne le connait.
    const resolue =
      column.reference === undefined
        ? {}
        : referenceDeCellule(column.reference, value, annuaires, liensMultiples(column.valueType, column.reglages));
    const node = column.render ? column.render(record) : (resolue.node ?? cellToText(value));
    // UNE COLONNE A CATALOGUE PORTE UNE CLEF : le catalogue en fait un mot, par
    // la meme regle et pour la meme raison qu'une reference (`ProjectedCell`).
    // La reference passe devant : une colonne n'est jamais les deux, et si elle
    // l'etait, le nom de la fiche designee est le plus precis des deux.
    const label = resolue.label ?? libelleDeCatalogue(column, value);
    row.cells[column.key] = {
      value,
      node,
      // Une colonne sans `set()` reste en lecture seule : la fiche ne pose
      // alors aucune affordance, pas meme au survol.
      editable: typeof column.set === 'function',
      ...(label === undefined ? {} : { label }),
      ...(column.optionsPour === undefined ? {} : { options: column.optionsPour(record) }),
    };
    if (column.totalLie === true) {
      const texte = typeof node === 'string' ? node : cellToText(value);
      if (texte !== '') (row.totauxLies ??= []).push(texte);
    }
  }

  return row;
}

/**
 * Projette un jeu d'enregistrements. L'ordre d'entree est conserve.
 *
 * `newRecords` est le SEUL endroit du produit ou la pastille "Nouveau" se
 * calcule : une vue la lit sur la ligne (`row.isNew`), elle ne la deduit
 * jamais elle-meme. Omis (ou reperage eteint dans les reglages) : toutes les
 * lignes sortent avec `isNew: false`, et `createdAt` n'est meme pas lu.
 *
 * UN SEUL booleen sort d'ici, `isNew` (creee apres la date de reference ET
 * jamais ouverte). La date n'est lue qu'une fois par ligne.
 */
export function projectRecords<T>(
  records: readonly T[],
  getId: (record: T) => string,
  binding: ViewBinding<T> | undefined,
  newRecords?: NewRecordsInput,
  /** Les annuaires charges, pour les colonnes de REFERENCE (voir `projectRecord`). */
  annuaires?: Annuaires,
): ProjectedRow[] {
  return records.map((record) => {
    const id = getId(record);
    /* `isRecordRecent` sert encore, mais SEULEMENT a calculer `isNew` : la
       ligne projetee ne porte plus de booleen "recent". Le repli qui s'en
       servait a ete retire (voir core/NewBadge.tsx). */
    const isNew =
      isRecordRecent(record, newRecords) && newRecords != null && !newRecords.seen.has(id);
    return projectRecord(record, id, binding, isNew, annuaires);
  });
}

/**
 * LA PROJECTION GARDEE D'UN RENDU A L'AUTRE, fiche par fiche.
 *
 * La projection d'une fiche (ses cellules, ses rendus, ses libelles) ne depend
 * que de la fiche, du binding, de la memoire du « nouveau » et des annuaires.
 * Une frappe dans la recherche ne change aucun des quatre : la refaire sur tout
 * le jeu a chaque lettre coutait l'essentiel de la frappe (profil du chantier
 * `ecran-fluide` : 785 ms sur 5 000 fiches, dont 600 pour le seul habillage
 * d'un taux). Le cache est une `WeakMap` par fiche : une fiche relue par le
 * chargeur est un objet neuf, donc projetee a neuf ; le cache entier tombe des
 * qu'un des trois autres termes change d'identite.
 */
export interface CacheDeProjection {
  binding: unknown;
  nouveau: unknown;
  annuaires: unknown;
  lignes: WeakMap<object, ProjectedRow>;
}

export function projectRecordsEnCache<T>(
  cache: CacheDeProjection | null,
  records: readonly T[],
  getId: (record: T) => string,
  binding: ViewBinding<T> | undefined,
  newRecords?: NewRecordsInput,
  annuaires?: Annuaires,
): { rows: ProjectedRow[]; cache: CacheDeProjection } {
  const valide =
    cache !== null && cache.binding === binding && cache.nouveau === newRecords && cache.annuaires === annuaires
      ? cache
      : { binding, nouveau: newRecords, annuaires, lignes: new WeakMap<object, ProjectedRow>() };
  const rows = records.map((record) => {
    const clef = typeof record === 'object' && record !== null ? (record as object) : null;
    const deja = clef === null ? undefined : valide.lignes.get(clef);
    if (deja !== undefined) return deja;
    const [ligne] = projectRecords([record], getId, binding, newRecords, annuaires);
    if (clef !== null && ligne !== undefined) valide.lignes.set(clef, ligne);
    return ligne as ProjectedRow;
  });
  return { rows, cache: valide };
}

/**
 * LE TEXTE D'UN ROLE DE PREMIERE LIGNE APRES UN PATCH. Un patch ecrit UNE
 * valeur : le role n'est nomme que s'il tient en UNE partie qui designe une
 * fiche (le cas d'un sous-titre « Client »). Un compose de plusieurs parties
 * ne se recompose pas d'une valeur seule : il garde le texte brut, que le
 * rechargement corrige. Sans partie, sans annuaire : la valeur, comme avant.
 */
function texteDuPatch<T>(value: Cell, field: FieldMap<T> | undefined, annuaires?: Annuaires): string {
  const partie = field?.parts?.length === 1 ? field.parts[0] : undefined;
  if (
    partie?.reference !== undefined &&
    (annuaires !== undefined || partie.reference.repli !== undefined)
  ) {
    const label = referenceDeCellule(partie.reference, value, annuaires).label;
    if (label !== undefined) return label;
  }
  return cellToText(value, field?.empty ?? '');
}

/**
 * LES JEUX QUE CES BINDINGS DESIGNENT, un par jeu : ce que l'hote doit charger
 * en annuaires pour que la projection sache NOMMER.
 *
 * LES ROLES ET LES COLONNES, ENSEMBLE. Une porte qui n'a son lien qu'en
 * sous-titre de liste, sans colonne dessus, ne chargeait rien, et le nom ne
 * venait jamais. UNE SEULE FABRIQUE, exportee : le moteur d'ecran, la carte
 * liee et le selecteur de rattachement faisaient chacun la meme boucle sur les
 * seules colonnes, et trois copies de « quels jeux ? » auraient diverge a la
 * premiere source de plus, ce qui est exactement ce qui vient d'arriver.
 *
 * DEUX SOURCES QUI DESIGNENT LE MEME JEU PARTAGENT LE MEME ANNUAIRE : la
 * premiere rencontree fait foi (toutes portent la meme reference, assemblee au
 * meme endroit).
 */
export function referencesDeBindings<T>(bindings: readonly ViewBinding<T>[]): ColumnReference[] {
  const parJeu = new Map<string, ColumnReference>();
  const retenir = (reference: ColumnReference | undefined): void => {
    if (reference === undefined) return;
    const cle = cleDeJeu(reference.family, reference.type);
    if (!parJeu.has(cle)) parJeu.set(cle, reference);
  };
  for (const binding of bindings) {
    for (const field of Object.values(rolesOf(binding))) {
      for (const part of field?.parts ?? []) {
        retenir(part.reference);
        /* LES ETAGES D'UNE LECTURE : chaque fiche liee qu'elle traverse se lit
           dans son annuaire (`Column.annuaires`). */
        for (const etage of part.annuaires ?? []) retenir(etage);
      }
    }
    for (const column of binding.fieldColumns ?? binding.columns ?? []) {
      retenir(column.reference);
      for (const etage of column.annuaires ?? []) retenir(etage);
    }
  }
  return [...parJeu.values()];
}

/**
 * LES IDS QUE CES LIGNES DESIGNENT, par jeu : ce que l'annuaire doit pouvoir
 * NOMMER meme quand la fiche est hors de sa fenetre (`souhaiterTitres`,
 * `lib/annuaires.tsx`).
 *
 * LA MEME LECTURE QUE LA PROJECTION : les colonnes de reference (`column.value`,
 * plusieurs ids par cellule pour un champ `liens`) ET les parties de role
 * (`part.get`), les deux endroits ou `referenceDeCellule` nomme un id. Les
 * references a repli (personnes) n'y sont pas : leur annuaire est toujours
 * complet. Les ids sont dedoublonnes par jeu ; une valeur vide n'en est pas un.
 */
export interface IdsVoulus {
  reference: ColumnReference;
  ids: ReadonlySet<string>;
}

export function idsDesReferences<T>(
  records: readonly T[],
  bindings: readonly ViewBinding<T>[],
): IdsVoulus[] {
  const parJeu = new Map<string, { reference: ColumnReference; ids: Set<string> }>();
  const recueillir = (reference: ColumnReference | undefined, value: Cell | undefined, plusieurs: boolean): void => {
    if (reference === undefined || reference.repli !== undefined || isBlank(value)) return;
    const cle = cleDeJeu(reference.family, reference.type);
    let jeu = parJeu.get(cle);
    if (jeu === undefined) {
      jeu = { reference, ids: new Set() };
      parJeu.set(cle, jeu);
    }
    for (const id of plusieurs ? lignesDeCellule(value) : [String(value)]) if (id !== '') jeu.ids.add(id);
  };
  for (const binding of bindings) {
    const parties = Object.values(rolesOf(binding)).flatMap((field) => field?.parts ?? []);
    const colonnes = binding.fieldColumns ?? binding.columns ?? [];
    for (const record of records) {
      for (const part of parties) recueillir(part.reference, part.get(record), false);
      for (const column of colonnes) {
        if (column.reference !== undefined) {
          recueillir(column.reference, column.value(record), liensMultiples(column.valueType, column.reglages));
        }
      }
    }
  }
  return [...parJeu.values()];
}

/**
 * Applique un patch OPTIMISTE sur une ligne deja projetee : la vue voit le
 * changement tout de suite, le core remet en place si l'ecriture echoue.
 * Retourne une NOUVELLE ligne (les vues comparent par identite).
 */
export function applyRolePatch<T>(
  row: ProjectedRow,
  role: string,
  value: Cell,
  field: FieldMap<T> | undefined,
  /**
   * LES ANNUAIRES CHARGES, pour un titre ou un sous-titre qui DESIGNE une
   * fiche : le nom choisi s'affiche tout de suite, pas son id en attendant le
   * serveur. Facultatif, absent chez les appelants d'avant : l'id, comme avant.
   */
  annuaires?: Annuaires,
): ProjectedRow {
  const patched: ProjectedRow = { ...row };
  switch (role) {
    case 'title':
      patched.title = texteDuPatch(value, field, annuaires) || row.id;
      break;
    case 'subtitle':
      patched.subtitle = texteDuPatch(value, field, annuaires);
      break;
    case 'avatar':
      patched.avatar = isBlank(value) ? undefined : cellToText(value);
      break;
    case 'couleur':
      patched.couleur = estCouleur(value) ? value : undefined;
      break;
    case 'photo':
      patched.photo = typeof value === 'string' && value.trim() !== '' ? value : undefined;
      break;
    case 'status':
      patched.status = resolveOption(value, field);
      break;
    case 'groupKey':
      patched.groups = [optionNommee(value, field, annuaires)];
      break;
    case 'lane':
      patched.lanes = [optionNommee(value, field, annuaires)];
      break;
    case 'dateStart':
      patched.start = toDate(value);
      /* L'INSTANT RESTE UN INSTANT apres le geste : le fuseau suit la valeur. */
      if (patched.start !== undefined && field?.instant !== undefined) patched.startFuseau = field.instant.fuseau;
      else delete patched.startFuseau;
      break;
    case 'dateEnd':
      patched.end = toDate(value);
      break;
    default:
      patched.extra = { ...row.extra, [role]: value };
      break;
  }
  return patched;
}

/**
 * Le jumeau d'`applyRolePatch` pour une COLONNE. Meme role, meme contrat de
 * retour (une nouvelle ligne, les vues comparent par identite), deux details
 * qui ne se devinent pas :
 *
 * 1. LA COPIE EST PROFONDE JUSQU'A LA CELLULE. `applyRolePatch` fait un
 *    `{ ...row }` plat : le sac `cells` y reste PARTAGE avec la ligne d'origine.
 *    Ecrire dedans muterait une ligne que d'autres resultats de memo tiennent
 *    encore. On clone donc `cells` PUIS la cellule visee.
 * 2. LE NODE OPTIMISTE EST BRUT (`cellToText`), jamais `column.render(record)` :
 *    le record en memoire porte encore l'ANCIENNE valeur, rappeler `render()`
 *    reafficherait l'ancien texte formate. L'habillage de l'app ("290 EUR", une
 *    date en francais) revient au `reload()` qui suit l'ecriture reussie. Ne pas
 *    "corriger" en rappelant `render()`.
 *
 * Une cle inconnue de `cells` ne fabrique RIEN : la colonne n'est pas declaree
 * dans le binding de la vue courante, il n'y a nulle part ou l'afficher.
 */
export function applyColumnPatch(
  row: ProjectedRow,
  key: string,
  value: Cell,
  options?: Option[],
  /**
   * LE LIBELLE DE LA FICHE CHOISIE, pour une colonne de REFERENCE : la valeur
   * ecrite est un ID, et le libelle de l'ancienne fiche ne vaut plus rien des
   * que la nouvelle est choisie.
   *
   * ABSENT, LE LIBELLE TOMBE plutot qu'il ne survit : une case qui garderait
   * « Sony FX9 » apres qu'on a choisi une autre fiche mentirait jusqu'au
   * rechargement, ce qui est pire que l'id nu qu'elle montre entre-temps.
   */
  libelle?: string,
  /**
   * LE RENDU DE LA VALEUR SEULE (`Column.rendreValeur`) : la case optimiste se
   * dessine alors EXACTEMENT comme la case confirmee (la pastille de couleur
   * d'un choix, tout de suite). Absent, ou pour une reference nommee
   * (`libelle`), la case montre le mot, comme avant.
   */
  rendre?: (valeur: Cell) => ReactNode,
): ProjectedRow {
  const cell = row.cells[key];
  if (cell == null) return row;
  /*
   * Une colonne a CATALOGUE affiche un libelle, pas la valeur stockee. Sans
   * cette resolution, choisir "Optique" montrait `optique` le temps de
   * l'aller-retour serveur, puis `Optique` au rechargement : un clignotement a
   * chaque choix, exactement la ou l'utilisateur regarde.
   *
   * `render()` ne peut pas servir ici : il prend le RECORD, qui porte encore
   * l'ancienne valeur tant que le serveur n'a pas repondu. Le catalogue, lui,
   * traduit la valeur seule.
   */
  const label = libelle ?? options?.find((option) => option.value === String(value ?? ''))?.label;
  /* `label` EST RECONSTRUIT, jamais herite : `...cell` recopierait celui de la
     fiche liee PRECEDENTE, et la case afficherait l'ancien nom sur le nouvel
     id, jusqu'au rechargement. */
  const { label: _ancien, ...garde } = cell;
  return {
    ...row,
    cells: {
      ...row.cells,
      [key]: {
        ...garde,
        value,
        node: libelle === undefined && rendre !== undefined ? rendre(value) : (label ?? cellToText(value)),
        ...(libelle === undefined ? {} : { label: libelle }),
        /* Le LIBELLE du catalogue reste porte par la cellule : c'est lui que la
           recherche et le tri lisent, le rendu n'etant qu'un noeud. */
        ...(libelle === undefined && rendre !== undefined && label !== undefined ? { label } : {}),
      },
    },
  };
}

/**
 * Recherche COTE CLIENT (lot 1) : le texte est confronte a ce que la ligne
 * MONTRE (titre, sous-titre, statut, valeurs de colonnes). Chercher sur ce qui
 * est affiche est la seule regle qu'un utilisateur peut deviner. Sans casse ni
 * accents (`searchField`), des DEUX cotes : « ecole » trouve « École ».
 */
export function matchesSearch(row: ProjectedRow, term: string): boolean {
  const needle = searchField(term.trim());
  if (needle === '') return true;
  return textesDeRecherche(row).some((text) => text.includes(needle));
}

/**
 * LES TEXTES QUE LA RECHERCHE LIT SUR UNE LIGNE, DEJA NORMALISES (sans casse ni
 * accents), GARDES PAR LIGNE. Une ligne projetee ne change pas d'une frappe a
 * l'autre (`projectRecordsEnCache`) : la normaliser a chaque lettre refaisait
 * des dizaines de milliers de normalisations par frappe sur 10 000 fiches
 * (chantier `ecran-fluide`). Une ligne patchee ou reprojetee est un objet neuf,
 * donc relue.
 */
const TEXTES_DE_RECHERCHE = new WeakMap<ProjectedRow, string[]>();

function textesDeRecherche(row: ProjectedRow): string[] {
  const deja = TEXTES_DE_RECHERCHE.get(row);
  if (deja !== undefined) return deja;
  const haystack: string[] = [row.title];
  if (row.subtitle != null) haystack.push(row.subtitle);
  if (row.status != null) haystack.push(row.status.label);
  for (const group of row.groups) haystack.push(group.label);
  for (const lane of row.lanes) haystack.push(lane.label);
  /* LA CELLULE ENTIERE, pas sa valeur : une cellule de REFERENCE porte un id
     en valeur et le NOM de la fiche liee en libelle. Chercher sur l'id ferait
     rater le mot qui est a l'ecran, ce qui est exactement le contraire de la
     regle ci-dessus. */
  for (const cell of Object.values(row.cells)) haystack.push(cellToText(cell));
  const textes = haystack.map((text) => searchField(text));
  TEXTES_DE_RECHERCHE.set(row, textes);
  return textes;
}

/**
 * La valeur comparable d'une ligne pour une cle de tri : une colonne d'abord,
 * sinon un role connu. Sans correspondance, la ligne ne bouge pas.
 */
export function sortValueOf(row: ProjectedRow, key: string): Cell | undefined {
  const cell = row.cells[key];
  /* `label ?? value` : une colonne de REFERENCE ou a CATALOGUE se trie sur le
     MOT AFFICHE, pas sur la clef stockee. Trier des ids Firestore (ou des
     valeurs anglaises) sous des libelles francais rend un ordre qui n'a l'air
     de rien, et rien ne le signale. */
  if (cell) return cell.label ?? cell.value;
  switch (key) {
    case 'title':
      return row.title;
    case 'subtitle':
      return row.subtitle ?? null;
    case 'status':
      return row.status?.label ?? null;
    case 'dateStart':
      return row.start ?? null;
    case 'dateEnd':
      return row.end ?? null;
    default:
      return row.extra[key];
  }
}

/** Les cles que `sortValueOf` resout SANS cellule : les roles de premiere classe. */
const SORT_ROLE_KEYS = new Set<string>(['title', 'subtitle', 'status', 'dateStart', 'dateEnd']);

/**
 * La vue ACTIVE sait-elle resoudre cette cle de tri toute seule ? Oui quand la
 * cle est un role de premiere classe (`sortValueOf` la lit sur la ligne), une
 * colonne du binding actif (la ligne porte sa cellule) ou un role libre (la
 * ligne porte sa valeur dans `extra`). Sinon, la cle appartient a un AUTRE
 * binding de la porte (le tri est a l'ECRAN, pas a la vue) et le core doit
 * fournir un acces de repli : voir le memo `projected` de useScreen.
 */
export function resolvesSortKey<T>(binding: ViewBinding<T> | undefined, key: string): boolean {
  if (SORT_ROLE_KEYS.has(key)) return true;
  if (binding == null) return false;
  if (binding.columns?.some((column) => column.key === key)) return true;
  const field = rolesOf(binding)[key];
  return field != null && !KNOWN_ROLES.has(key);
}

/* ------------------------------------------------------------------ */
/* L'avatar : resolu UNE fois, pour toutes les vues                     */
/* ------------------------------------------------------------------ */

/**
 * Le role `avatar` porte "une URL ou des initiales, selon ce que l'ecran
 * fournit" (contrat). On tranche sur la FORME de la chaine, jamais sur une
 * convention cachee : tout ce qui ressemble a une ressource est charge comme
 * une image (http(s), data:, blob:, chemin absolu).
 */
const IMAGE_SOURCE = /^(?:https?:\/\/|data:|blob:|\/)/i;

export function isImageSource(value: string): boolean {
  return IMAGE_SOURCE.test(value);
}

/**
 * Initiales : deux lettres au maximum, sinon le cercle deborde. Plusieurs mots
 * donnent leurs premieres lettres ("Jean Dupont" -> "JD"), un mot seul ses deux
 * premieres ("Minou" -> "MI"), une valeur deja courte reste telle quelle
 * ("AB" -> "AB").
 */
export function initialsOf(text: string): string {
  const words = text
    .trim()
    .split(/\s+/)
    .filter((word) => word !== '');
  const [first, second] = words;
  if (first == null) return '';
  if (second != null) return (first.charAt(0) + second.charAt(0)).toUpperCase();
  return first.slice(0, 2).toUpperCase();
}

/** Une pastille de tete de ligne, resolue : soit une image, soit des initiales. */
export interface AvatarSlot {
  /** L'image a charger. Absente quand la pastille tombe sur les initiales. */
  src?: string;
  /** Les initiales a centrer dans le cercle (jamais la chaine brute). */
  initials: string;
}

/**
 * LA PASTILLE D'UNE LIGNE, resolue UNE SEULE FOIS pour toutes les vues.
 *
 * Chaque vue decidait avant si le role `avatar` etait une image ou des
 * initiales, avec sa propre regle : trois tests differents et deux facons de
 * fabriquer les initiales, donc la meme donnee rendait trois pastilles selon la
 * vue. La regle vit ici, et nulle part ailleurs :
 * - valeur qui ressemble a une ressource : c'est l'image ;
 * - toute autre valeur : ses initiales ;
 * - valeur absente sur CETTE ligne : les initiales du titre, jamais un rond
 *   vide au milieu d'une colonne de visages.
 *
 * Si l'ecran n'a pas declare le role du tout, la vue ne dessine simplement pas
 * de pastille : c'est `hasAvatarRole` qui tranche, au niveau du JEU, jamais
 * ligne par ligne (sinon la colonne des titres danse d'une ligne a l'autre).
 */
export function avatarOf(row: ProjectedRow): AvatarSlot {
  const value = row.avatar;
  if (value != null && value !== '' && isImageSource(value)) {
    return { src: value, initials: initialsOf(row.title) };
  }
  return { initials: initialsOf(value != null && value !== '' ? value : row.title) };
}

/**
 * L'ecran a-t-il projete le role `avatar` sur ce jeu ? La question se pose au
 * niveau de la LISTE (hauteur de ligne fixe, titres alignes), pas de la ligne.
 */
export function hasAvatarRole(rows: readonly ProjectedRow[]): boolean {
  return rows.some((row) => row.avatar != null);
}

/**
 * La liste, le tableau et le kanban dessinent-ils une pastille ? Oui des que
 * l'ecran a projete le role `avatar` (`hasAvatarRole`), OU que le jeu porte la
 * couleur ou la photo (`ProjectedRow.identite`, pose par la projection sur
 * chaque ligne d'un binding qui les a). Meme regle, meme niveau : le JEU.
 */
export function hasPastille(rows: readonly ProjectedRow[]): boolean {
  return hasAvatarRole(rows) || rows.some((row) => row.identite === true);
}

/**
 * Le catalogue COMPLET d'un role, tel que la vue le recoit :
 * celui du tenant, PUIS les valeurs hors catalogue effectivement presentes.
 *
 * C'est la garantie "un statut hors catalogue ne fait jamais disparaitre une
 * ligne" : au lieu d'un seul fourre-tout, chaque valeur inconnue
 * garde sa colonne, en ton neutre, apres le catalogue. L'option fourre-tout
 * (`OTHER_LABEL`) n'apparait que si des lignes n'ont AUCUNE valeur pour ce role.
 */
export function catalogOf<T>(
  role: string,
  field: FieldMap<T> | undefined,
  rows: readonly ProjectedRow[],
): Option[] {
  const catalog = field?.options?.() ?? [];
  const known = new Set(catalog.map((option) => option.value));
  const extras: Option[] = [];
  const seen = new Set<string>();

  const collect = (option: Option): void => {
    if (known.has(option.value) || seen.has(option.value)) return;
    seen.add(option.value);
    extras.push(option);
  };

  for (const row of rows) {
    if (role === 'status' && row.status) collect(row.status);
    else if (role === 'groupKey') for (const group of row.groups) collect(group);
    else if (role === 'lane') for (const lane of row.lanes) collect(lane);
  }

  return [...catalog, ...extras];
}

/**
 * LE CATALOGUE D'UNE CLEF, tel que la vue le recoit (`ViewProps.optionsOf`) :
 * les ROLES d'abord, les COLONNES ensuite.
 *
 * L'ORDRE COMPTE quand une colonne et un role portent le meme nom (une colonne
 * `status` n'est pas le role `status`) : c'est l'inverse de `sortValueOf`, qui
 * sert le tri et donne la priorite aux colonnes. Les deux ordres existaient
 * deja, celui-ci n'est ecrit qu'ici depuis que DEUX surfaces le posent : le
 * moteur d'ecran pour la vue active, et la CARTE LIEE pour la porte liee, dont
 * les catalogues ne sont pas ceux de la porte parente.
 *
 * SANS ROLE NI COLONNE A CATALOGUE, on rend quand meme les valeurs HORS
 * catalogue rencontrees : personne ne perd sa ligne parce que sa valeur n'etait
 * pas prevue.
 */
export function optionsOfBinding<T>(
  binding: ViewBinding<T> | undefined,
  rows: readonly ProjectedRow[],
  key: string,
): Option[] {
  const field = binding == null ? undefined : rolesOf(binding)[key];
  if (field != null) return catalogOf(key, field, rows);
  const column = binding?.columns?.find((candidate) => candidate.key === key);
  if (column?.options != null) return column.options();
  return catalogOf(key, undefined, rows);
}

/* ------------------------------------------------------------------ */
/* Le MODE MODELES : le NOM du modele par-dessus le titre projete      */
/* ------------------------------------------------------------------ */

/**
 * LES LIGNES DE MODELES, RENOMMEES PAR LEUR MODELE.
 *
 * En mode Modeles, chaque ligne est la projection d'une PSEUDO-FICHE
 * (`pseudoFicheDeModele`, `lib/templates.tsx`) : les champs enregistres dans le
 * modele, projetes par le binding ORDINAIRE de la porte, comme s'il s'agissait
 * d'une fiche. Le titre qui en sort est donc le titre du CONTENU du modele (la
 * marque d'une camera, le nom d'un chantier), et pas ce que l'utilisateur a
 * tape en enregistrant le modele. Or dans une liste de modeles, ce qu'on
 * cherche est le NOM DU MODELE : c'est lui qui les distingue, et deux modeles
 * partis de la meme fiche porteraient sinon exactement le meme titre.
 *
 * POURQUOI EN POST-PROJECTION, ET PAS DANS LE BINDING. `projectRecord` calcule
 * `row.title` par une closure de role (`roles.title.get`) que le socle ne peut
 * ni lire ni remplacer : elle appartient a l'app. On ecrase donc APRES, au meme
 * endroit et de la meme facon qu'`applyRolePatch` ecrase un titre pour
 * l'optimisme. Le reste de la ligne (cellules, sous-titre, etiquettes, statut)
 * decrit bien le CONTENU du modele et ne bouge pas : c'est ce contenu qu'on
 * vient modifier.
 *
 * UN NOM VIDE NE REMPLACE RIEN : une ligne sans identite est illisible, et le
 * titre projete (a defaut, l'id) reste toujours meilleur que du blanc. Un id
 * absent de la map garde son titre, sans exception : c'est ce qui rend cette
 * fonction sans effet hors du mode.
 *
 * NE MUTE RIEN, meme doctrine que `lignesEnLectureSeule` : les lignes viennent
 * d'une projection memoisee, les muter ferait mentir tout ce qui la relit sans
 * repasser par ici.
 */
export function nommerLignesDeModeles(
  rows: readonly ProjectedRow[],
  noms: ReadonlyMap<string, string>,
): ProjectedRow[] {
  return rows.map((row) => {
    const nom = noms.get(row.id);
    if (nom == null || nom === '') return row;
    return { ...row, title: nom };
  });
}
